import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { formatDateTime, intlLocale } from "../lib/format";
import { PanelLoading } from "../components/Loading";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

/** The translator as react-i18next hands it over, so a helper can take it without widening its type. */
type Translate = ReturnType<typeof useTranslation>["t"];
import {
  COMPLAINT_KINDS, complaintsApi, nextAllowedComplaint,
  type Complaint, type ComplaintAttachment, type ComplaintKind, type ComplaintMessage, type ComplaintStatus,
  type ComplaintStatusHistoryEntry,
} from "../api/complaints";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { useConfirm } from "../components/ConfirmDialog";
import { ComplaintKindTag, ComplaintStatusBadge, ComplaintUrgentTag } from "../components/ComplaintStatusBadge";
import { ComplaintAttachmentList } from "../components/ComplaintAttachmentList";
import { ComplaintDeadlineTag } from "../components/complaints/ComplaintDeadlineTag";
import { ComplaintTeamPanel } from "../components/complaints/ComplaintTeamPanel";
import { ComplaintCorrectiveActions } from "../components/complaints/ComplaintCorrectiveActions";
import { ComplaintEightD } from "../components/complaints/ComplaintEightD";
import { ComplaintSettlementPanel } from "../components/complaints/ComplaintSettlementPanel";
import Ic from "../components/icons.jsx";
import { validateTransitionReason } from "./complaintsFilter";
import { lotsApi, type ReceivingLot } from "../api/lots";
import { Select } from "../components/Select";

/** The course a complaint runs: filed, in review, answered, closed. A rejection is the same course with
 * a different answer, so the stepper stops on the answer step and the badge says which answer it was. */
const WF_STEPS: ComplaintStatus[] = ["SUBMITTED", "IN_REVIEW", "RESOLVED", "CLOSED"];

/**
 * Reklamacija detail — one page for both sides of the portal (mirrors
 * DeliveryDetailPage's shape): messages thread + attachments are visible to
 * anyone who can open the page; status-transition actions are gated on
 * `complaints:manage` (SALES/SALES_DIRECTOR/KVALITET/ADMIN/SUPERADMIN —
 * never CUST_*, matching the backend gate).
 */
export default function ComplaintDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  // Lot linking rides on the same permission as any other complaint change, and
  // needs the lot list to offer a choice. The lot itself opens only with lots:view.
  const canOpenLot = hasPermission("lots:view");
  const canLinkLot = hasPermission("complaints:manage") && canOpenLot;
  const [lots, setLots] = useState<ReceivingLot[]>([]);

  const [complaint, setComplaint] = useState<Complaint | null>(null);
  const [messages, setMessages] = useState<ComplaintMessage[]>([]);
  const [attachments, setAttachments] = useState<ComplaintAttachment[]>([]);
  const [history, setHistory] = useState<ComplaintStatusHistoryEntry[]>([]);
  const [busy, setBusy] = useState(false);

  const [messageDraft, setMessageDraft] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [decisionBasis, setDecisionBasis] = useState("");
  const [internalNote, setInternalNote] = useState(false);
  const [showContestForm, setShowContestForm] = useState(false);
  const [contestReason, setContestReason] = useState("");

  const canManage = hasPermission("complaints:manage");
  const canContribute = hasPermission("complaints:write") || canManage;
  // KK53 gives the ruling to a director, and the backend narrows further by kind. The page offers the
  // buttons to anyone holding the key and lets the backend have the last word on which kind is theirs.
  const canDecide = hasPermission("complaints:decide");
  const canNameTeam = hasPermission("complaints:team");
  const canRunEightD = hasPermission("complaints:eight_d");
  const canApproveAmount = hasPermission("complaints:outcome_approve");
  // A complaint never embeds order pricing,
  // but the "open order" link must additionally be gated on orders:view so a
  // KVALITET user (who lacks it) can't click through to the priced order.
  const canOpenOrder = hasPermission("orders:view");

  async function loadMessages() {
    if (!id) return;
    try {
      setMessages(await complaintsApi.listMessages(id));
    } catch {
      // best-effort — the thread just stays empty
    }
  }
  async function loadAttachments() {
    if (!id) return;
    try {
      setAttachments(await complaintsApi.listAttachments(id));
    } catch {
      // best-effort
    }
  }
  async function loadHistory() {
    if (!id) return;
    try {
      setHistory(await complaintsApi.history(id));
    } catch {
      // best-effort — the stepper above still shows where the complaint stands
    }
  }

  // Combined initial load — messages/attachments are best-effort (a failure
  // there shouldn't block the complaint itself from rendering).
  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      setComplaint(await complaintsApi.get(id));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
    await loadMessages();
    await loadAttachments();
    await loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the loaders are stable per id, redeclared each render but not memoized
  }, [id, toast]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Best effort: a caller without lots:view simply gets no picker.
  useEffect(() => {
    if (!canLinkLot) return;
    lotsApi.list().then(setLots).catch(() => setLots([]));
  }, [canLinkLot]);

  async function linkLot(lotId: string | null) {
    if (!id) return;
    try {
      await complaintsApi.linkLot(id, lotId);
      await refresh();
    } catch (e) {
      const err = e as ApiError;
      toast.error(err.code === "LOT_NOT_FOUND"
        ? t("complaints.error.lotNotFound", "That lot no longer exists. Reload the page and pick again.")
        : err.message);
    }
  }

  if (!complaint) {
    return <PanelLoading text={t("common.loading")} />;
  }

  const allowed = nextAllowedComplaint(complaint.status);

  async function sendMessage() {
    if (!id || !messageDraft.trim()) return;
    setSendingMessage(true);
    try {
      await complaintsApi.addMessage(id, messageDraft.trim(), internalNote);
      setMessageDraft("");
      setInternalNote(false);
      await loadMessages();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setSendingMessage(false);
    }
  }

  async function doTransition(to: ComplaintStatus, reason?: string, basis?: string) {
    if (!id) return;
    setBusy(true);
    try {
      const updated = await complaintsApi.transition(id, to, reason, basis);
      setComplaint(updated);
      await loadHistory();
      toast.success(t("complaints.toast.transitioned", "Status updated."));
    } catch (err) {
      toast.error(transitionErrorText(t, err as ApiError));
    } finally {
      setBusy(false);
    }
  }

  async function doStartReview() {
    await doTransition("IN_REVIEW");
  }

  async function doResolve() {
    if (!(await confirm({ message: t("complaints.confirm.resolve", "Mark this complaint as resolved?") }))) return;
    await doTransition("RESOLVED", undefined, decisionBasis.trim() || undefined);
    setDecisionBasis("");
  }

  async function doClose() {
    if (!(await confirm({ message: t("complaints.confirm.close", "Zatvoriti reklamaciju?") }))) return;
    await doTransition("CLOSED");
  }

  async function submitReject(e: FormEvent) {
    e.preventDefault();
    if (validateTransitionReason("REJECTED", rejectReason)) {
      toast.error(t("complaints.error.reasonRequired", "A reject reason is required."));
      return;
    }
    if (!(await confirm({ message: t("complaints.confirm.reject", "Reject this complaint? This cannot be undone."), danger: true }))) return;
    await doTransition("REJECTED", rejectReason.trim(), decisionBasis.trim() || undefined);
    setShowRejectForm(false);
    setRejectReason("");
    setDecisionBasis("");
  }

  async function submitContest(e: FormEvent) {
    e.preventDefault();
    if (!id || !contestReason.trim()) return;
    setBusy(true);
    try {
      const created = await complaintsApi.reopen(id, contestReason.trim());
      setShowContestForm(false);
      setContestReason("");
      toast.success(t("complaints.toast.contested", "Otvorena je nova reklamacija."));
      navigate(`/complaints/${created.id}`);
    } catch (err) {
      toast.error(transitionErrorText(t, err as ApiError));
    } finally {
      setBusy(false);
    }
  }

  async function changeKind(kind: ComplaintKind | "") {
    if (!id) return;
    setBusy(true);
    try {
      setComplaint(await complaintsApi.classify(id, { kind: kind || null, urgent: complaint?.urgent }));
    } catch (err) {
      toast.error(transitionErrorText(t, err as ApiError));
    } finally {
      setBusy(false);
    }
  }

  async function toggleUrgent() {
    if (!id || !complaint) return;
    setBusy(true);
    try {
      setComplaint(await complaintsApi.classify(id, { kind: complaint.kind, urgent: !complaint.urgent }));
    } catch (err) {
      toast.error(transitionErrorText(t, err as ApiError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label={t("complaintDetail.breadcrumb.aria", "Breadcrumb")}>
            <span><Link to="/complaints" style={crumbLink}>{t("complaints.list.title", "Complaints")}</Link></span>
            <span style={crumbCurrent}>{complaint.title}</span>
          </nav>
          <h1>{complaint.title}</h1>
          <div className="sub">
            <code style={{ fontSize: 12.5 }}>{complaint.caseNumber}</code> · {complaint.companyName} ·{" "}
            {t("complaints.detail.filedOn", "filed {{date}}", { date: formatDate(complaint.createdAt) })}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
            <ComplaintKindTag kind={complaint.kind} />
            <ComplaintUrgentTag urgent={complaint.urgent} />
            {complaint.reopenedFromId && complaint.reopenedFromCaseNumber && (
              <Link to={`/complaints/${complaint.reopenedFromId}`} className="tag neutral" style={{ textDecoration: "none" }}>
                {t("complaints.detail.contestOf", "osporava {{case}}", { case: complaint.reopenedFromCaseNumber })}
              </Link>
            )}
            {complaint.reopenedAsId && complaint.reopenedAsCaseNumber && (
              <Link to={`/complaints/${complaint.reopenedAsId}`} className="tag warn" style={{ textDecoration: "none" }}>
                {t("complaints.detail.contestedBy", "osporena kroz {{case}}", { case: complaint.reopenedAsCaseNumber })}
              </Link>
            )}
          </div>
        </div>
        <div className="page-actions">
          <button type="button" onClick={() => navigate(-1)} className="btn">
            {t("orderEditor.back", "Back")}
          </button>
        </div>
      </div>

      <div style={kpiStrip}>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("complaints.kpi.status", "Status")}</span>
          <strong style={kpiValueLg}><ComplaintStatusBadge status={complaint.status} /></strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("complaints.field.order", "Order")}</span>
          <strong style={kpiValueLg}>
            {complaint.orderId && complaint.orderNumber ? (
              canOpenOrder ? (
                <Link to={`/orders/${complaint.orderId}`} style={{ color: "var(--brand-primary)" }}>
                  {complaint.orderNumber}
                </Link>
              ) : (
                <code style={{ fontSize: 14 }}>{complaint.orderNumber}</code>
              )
            ) : (
              <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>—</span>
            )}
          </strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("complaints.field.lot", "LOT")}</span>
          <strong style={kpiValueLg}>
            {complaint.lot ?? <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>—</span>}
          </strong>
        </div>
        {canManage && (
          <div style={kpiTile}>
            <span style={kpiLabel}>{t("complaints.field.receivingLot", "Intake lot")}</span>
            <strong style={kpiValueLg}>
              {complaint.receivingLotId && complaint.receivingLotNumber ? (
                canOpenLot ? (
                  <Link to={`/procurement/receiving-lots/${complaint.receivingLotId}`} style={{ color: "inherit" }}>
                    {complaint.receivingLotNumber}
                  </Link>
                ) : (
                  <code style={{ fontSize: 14 }}>{complaint.receivingLotNumber}</code>
                )
              ) : (
                <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>—</span>
              )}
            </strong>
            {canLinkLot && lots.length > 0 && (
              <Select<string>
                value={complaint.receivingLotId ?? ""}
                onChange={(v) => void linkLot(v || null)}
                ariaLabel={t("complaints.field.receivingLot", "Intake lot")}
                options={[
                  { value: "", label: t("complaints.field.noLot", "— not linked —") },
                  ...lots.map((l) => ({ value: l.id, label: l.lotNumber })),
                ]}
              />
            )}
          </div>
        )}
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("complaints.kpi.firstResponse", "Prvi odgovor")}</span>
          <strong style={kpiValueLg}>
            <ComplaintDeadlineTag dueAt={complaint.dueFirstResponseAt} answeredAt={complaint.firstResponseAt} />
          </strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("complaints.kpi.finalResponse", "Konačan odgovor")}</span>
          <strong style={kpiValueLg}>
            <ComplaintDeadlineTag dueAt={complaint.dueFinalAt} answeredAt={complaint.finalResponseAt} />
          </strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("complaints.kpi.updated", "Last updated")}</span>
          <strong style={kpiValueLg}>{formatDate(complaint.updatedAt)}</strong>
        </div>
      </div>

      <div className="order-layout">
        <section style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <section style={card}>
            <h2 style={cardTitle}>{t("complaints.section.description", "Description")}</h2>
            <p style={{ margin: 0, fontSize: 13.5, color: "var(--ink-2)", whiteSpace: "pre-wrap" }}>
              {complaint.description || t("complaints.field.noDescription", "No description provided.")}
            </p>
          </section>

          {(complaint.claimedQuantity || complaint.goodsReceivedOn || complaint.goodsLotId) && (
            <section style={card}>
              <h2 style={cardTitle}>{t("complaints.section.goods", "Roba")}</h2>
              <div className="muted">
                {complaint.claimedQuantity && (
                  <div>
                    {t("complaints.field.claimedQuantity", "Reklamirana količina")}: {complaint.claimedQuantity}{" "}
                    {complaint.claimedUnit ?? ""}
                  </div>
                )}
                {complaint.goodsReceivedOn && (
                  <div>
                    {t("complaints.field.goodsReceivedOn", "Roba primljena")}: {formatDate(complaint.goodsReceivedOn)}
                  </div>
                )}
                {complaint.goodsLotId && (
                  <div>
                    {t("complaints.field.goodsLot", "Lot otpreme")}: <code>{complaint.goodsLotId.slice(0, 8)}</code>
                  </div>
                )}
              </div>
            </section>
          )}

          {(complaint.decidedAt || complaint.decisionBasis) && (
            <section style={card}>
              <h2 style={cardTitle}>{t("complaints.section.decision", "Odluka")}</h2>
              <div className="muted">
                {complaint.decidedAt && (
                  <div>
                    {formatDateTime(complaint.decidedAt)}
                    {complaint.decidedByName ? ` · ${complaint.decidedByName}` : ""}
                  </div>
                )}
              </div>
              {complaint.decisionBasis && (
                <p style={{ margin: 0, fontSize: 13.5, color: "var(--ink-2)", whiteSpace: "pre-wrap" }}>
                  {complaint.decisionBasis}
                </p>
              )}
            </section>
          )}

          {(hasPermission("section:complaint_outcome:read") || hasPermission("section:complaint_decisions:read")) && (
            <ComplaintSettlementPanel
              complaint={complaint}
              canSettle={canDecide}
              canApproveAmount={canApproveAmount}
              onChanged={setComplaint}
            />
          )}

          {hasPermission("section:complaint_team_8d:read") && <ComplaintTeamPanel complaintId={complaint.id} canManage={canNameTeam} />}

          {hasPermission("section:complaint_processing:read") && (
            <ComplaintCorrectiveActions
              complaintId={complaint.id}
              canManage={hasPermission("section:complaint_processing:write")}
              onChanged={() => void refresh()}
            />
          )}

          {hasPermission("section:complaint_team_8d:read") && (
            <ComplaintEightD complaint={complaint} canOpen={canRunEightD} onOpened={() => void refresh()} />
          )}

          <section style={card}>
            <h2 style={cardTitle}>{t("complaints.section.attachments", "Attachments")}</h2>
            <ComplaintAttachmentList
              complaintId={complaint.id}
              attachments={attachments}
              canUpload={canContribute}
              onUploaded={loadAttachments}
            />
          </section>

          <section style={card}>
            <h2 style={cardTitle}>{t("complaints.section.messages", "Messages")}</h2>
            {messages.length === 0 ? (
              <div style={{ color: "var(--ink-3)", fontSize: 13 }}>
                {t("complaints.messages.empty", "No messages yet.")}
              </div>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                {messages.map((m) => (
                  <li key={m.id} style={messageCard}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 12, color: "var(--ink-3)" }}>
                      <span style={avatar}>{m.authorName.slice(0, 2).toUpperCase()}</span>
                      <b style={{ color: "var(--ink)" }}>{m.authorName}</b>
                      <span>· {formatDateTime(m.createdAt)}</span>
                    </div>
                    {m.internal && (
                      <span className="tag warn" style={{ marginBottom: 4, display: "inline-block" }}>
                        {t("complaints.messages.internal", "interna beleška")}
                      </span>
                    )}
                    <div style={{ color: "var(--ink-2)", fontSize: 14, whiteSpace: "pre-wrap" }}>{m.body}</div>
                  </li>
                ))}
              </ul>
            )}

            {canContribute && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                <textarea
                  className="input"
                  value={messageDraft}
                  onChange={(e) => setMessageDraft(e.target.value)}
                  rows={3}
                  maxLength={5000}
                  disabled={sendingMessage}
                  style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }}
                  placeholder={t("complaints.messages.placeholder", "Write a message…")}
                />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  {canManage ? (
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--ink-3)" }}>
                      <input
                        type="checkbox"
                        checked={internalNote}
                        onChange={(e) => setInternalNote(e.target.checked)}
                        disabled={sendingMessage}
                      />
                      {t("complaints.messages.internalToggle", "Interna beleška — kupac ne vidi")}
                    </label>
                  ) : (
                    <span />
                  )}
                  <button
                    type="button"
                    className="btn primary sm"
                    onClick={() => void sendMessage()}
                    disabled={sendingMessage || !messageDraft.trim()}
                  >
                    {sendingMessage ? t("complaints.messages.sending", "Sending…") : t("complaints.messages.send", "Send")}
                  </button>
                </div>
              </div>
            )}
          </section>
        </section>

        <aside style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <section style={card}>
            <h2 style={cardTitle}>{t("complaints.timeline.title", "Status")}</h2>
            <WorkflowStepper status={complaint.status} />
            {history.length > 0 && <StatusHistory entries={history} />}
          </section>

          {canManage && complaint.decidedAt === null && (
            <section style={card}>
              <h2 style={cardTitle}>{t("complaints.section.classification", "Vrsta i prioritet")}</h2>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                {t(
                  "complaints.hint.classification",
                  "Vrsta određuje ko odlučuje o osnovanosti, pa se upisuje pre odluke.",
                )}
              </p>
              <Select<string>
                value={complaint.kind ?? ""}
                onChange={(v) => void changeKind(v as ComplaintKind | "")}
                ariaLabel={t("complaints.field.kind", "Vrsta")}
                options={[
                  { value: "", label: t("complaints.kind.none", "— nije određena —") },
                  ...COMPLAINT_KINDS.map((k) => ({
                    value: k,
                    label: t(`complaints.kind.${k}`, { defaultValue: k }),
                  })),
                ]}
              />
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--ink-2)" }}>
                <input type="checkbox" checked={complaint.urgent} disabled={busy} onChange={() => void toggleUrgent()} />
                {t("complaints.field.urgent", "Hitno")}
              </label>
            </section>
          )}

          {canManage && allowed.includes("IN_REVIEW") && (
            <section style={{ ...card, borderColor: "var(--brand-primary)", boxShadow: "0 0 0 3px var(--brand-primary-50)" }}>
              <h2 style={cardTitle}>{t("complaints.action.startReview", "Start review")}</h2>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                {t("complaints.hint.startReview", "Move this complaint into review — signals it's being worked on.")}
              </p>
              <button type="button" className="btn primary" onClick={() => void doStartReview()} disabled={busy}>
                <Ic.check /> {t("complaints.action.startReviewBtn", "U obradu")}
              </button>
            </section>
          )}

          {canDecide && allowed.includes("RESOLVED") && (
            <section style={{ ...card, borderColor: "var(--brand-primary)", boxShadow: "0 0 0 3px var(--brand-primary-50)" }}>
              <h2 style={cardTitle}>{t("complaints.action.resolve", "Prihvati")}</h2>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                {t(
                  "complaints.hint.resolve",
                  "Prihvatanje je odluka o osnovanosti. Posle nje se upisuju ishod i korektivna mera, pa se reklamacija zatvara.",
                )}
              </p>
              <Field label={t("complaints.field.decisionBasis", "Na čemu je odluka")}>
                <input
                  className="input"
                  value={decisionBasis}
                  onChange={(e) => setDecisionBasis(e.target.value)}
                  placeholder={t("complaints.field.decisionBasisPlaceholder", "Kontrauzorak, nalaz druge laboratorije, dokumenta…")}
                />
              </Field>
              <button type="button" className="btn primary" onClick={() => void doResolve()} disabled={busy}>
                <Ic.check /> {t("complaints.action.resolveBtn", "Prihvati")}
              </button>
            </section>
          )}

          {hasPermission("complaints:close") && allowed.includes("CLOSED") && (
            <section style={card}>
              <h2 style={cardTitle}>{t("complaints.action.close", "Zatvori")}</h2>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                {complaint.status === "RESOLVED"
                  ? t(
                      "complaints.hint.closeResolved",
                      "Osnovana reklamacija se zatvara kada je svaka korektivna mera sprovedena.",
                    )
                  : t("complaints.hint.closeRejected", "Odbijena reklamacija nema šta da koriguje i zatvara se odmah.")}
              </p>
              <button type="button" className="btn" onClick={() => void doClose()} disabled={busy}>
                {t("complaints.action.closeBtn", "Zatvori")}
              </button>
            </section>
          )}

          {canDecide && complaint.status === "CLOSED" && complaint.reopenedAsId === null && (
            <section style={card}>
              <h2 style={cardTitle}>{t("complaints.action.contest", "Kupac osporava")}</h2>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                {t(
                  "complaints.hint.contest",
                  "Otvara se nova reklamacija koja pokazuje iz koje je nastala. Zatvorena ostaje kakva je, da se oba odgovora čitaju jedan uz drugi.",
                )}
              </p>
              {!showContestForm ? (
                <button type="button" className="btn" onClick={() => setShowContestForm(true)}>
                  {t("complaints.action.contestBtn", "Otvori novu")}
                </button>
              ) : (
                <form onSubmit={submitContest} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <Field label={t("complaints.field.contestReason", "Čime kupac osporava")}>
                    <textarea
                      value={contestReason}
                      onChange={(e) => setContestReason(e.target.value)}
                      rows={3}
                      className="input"
                      style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }}
                    />
                  </Field>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" className="btn" onClick={() => setShowContestForm(false)}>
                      {t("action.cancel", "Odustani")}
                    </button>
                    <button type="submit" className="btn primary" disabled={busy || !contestReason.trim()}>
                      {t("complaints.action.contestBtn", "Otvori novu")}
                    </button>
                  </div>
                </form>
              )}
            </section>
          )}

          {canDecide && allowed.includes("REJECTED") && (
            <section style={card}>
              <h2 style={cardTitle}>{t("complaints.action.reject", "Reject")}</h2>
              {!showRejectForm ? (
                <button
                  type="button"
                  className="btn"
                  style={{ borderColor: "var(--err)", color: "var(--err)", background: "var(--surface)" }}
                  onClick={() => setShowRejectForm(true)}
                >
                  <Ic.x /> {t("complaints.action.rejectBtn", "Odbij")}
                </button>
              ) : (
                <form onSubmit={submitReject} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <Field label={t("complaints.field.rejectReason", "Reason")}>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={3}
                      className="input"
                      style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }}
                      placeholder={t("complaints.field.rejectReasonPlaceholder", "Why is this complaint being rejected?")}
                    />
                  </Field>
                  <Field label={t("complaints.field.decisionBasis", "Na čemu je odluka")}>
                    <input
                      className="input"
                      value={decisionBasis}
                      onChange={(e) => setDecisionBasis(e.target.value)}
                      placeholder={t("complaints.field.decisionBasisPlaceholder", "Kontrauzorak, nalaz druge laboratorije, dokumenta…")}
                    />
                  </Field>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" className="btn" onClick={() => setShowRejectForm(false)}>{t("action.cancel", "Cancel")}</button>
                    <button
                      type="submit"
                      className="btn"
                      style={{ borderColor: "var(--err)", color: "var(--err)", background: "var(--surface)" }}
                      disabled={busy}
                    >
                      {t("complaints.action.rejectBtn", "Odbij")}
                    </button>
                  </div>
                </form>
              )}
            </section>
          )}

          {complaint.status === "REJECTED" && (
            <section style={{ ...card, borderColor: "var(--err)" }}>
              <h2 style={{ ...cardTitle, color: "var(--err)" }}>{t("complaints.section.rejected", "Rejected")}</h2>
              <span style={fieldLabel}>{t("complaints.field.rejectReason", "Reason")}</span>
              <p style={{ margin: 0, fontSize: 13.5, color: "var(--ink-2)", whiteSpace: "pre-wrap" }}>
                {complaint.rejectReason ?? "—"}
              </p>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

function WorkflowStepper({ status }: { status: ComplaintStatus }) {
  const { t } = useTranslation();
  // A rejection reached the answer step; it is the answer that differed, not the course.
  const rejected = status === "REJECTED";
  const reached = rejected ? WF_STEPS.indexOf("RESOLVED") : WF_STEPS.indexOf(status);
  const labels = [
    t("complaints.timeline.step.submitted", "Submitted"),
    t("complaints.timeline.step.inReview", "In review"),
    t("complaints.timeline.step.resolved", "Resolved"),
    t("complaints.timeline.step.closed", "Zatvorena"),
  ];
  return (
    <ol className="wf">
      {labels.map((label, i) => {
        const state = i < reached ? "done" : i === reached ? (rejected ? "stopped" : "current") : "upcoming";
        return <li key={label} className={`wf-${state}`}>{label}</li>;
      })}
      <style>{`
        ol.wf{list-style:none;padding:0;margin:0}
        ol.wf li{position:relative;padding:0 0 18px 26px;color:var(--ink-4);font-size:13px}
        ol.wf li:last-child{padding-bottom:0}
        ol.wf li:not(:last-child):after{content:"";position:absolute;left:6px;top:16px;bottom:0;width:2px;background:var(--border)}
        ol.wf li.wf-done:not(:last-child):after{background:var(--brand-primary)}
        ol.wf li:before{content:"";position:absolute;left:0;top:2px;width:14px;height:14px;border-radius:50%;border:2px solid var(--border);background:var(--surface);box-sizing:border-box}
        ol.wf li.wf-done{color:var(--ink-2)}
        ol.wf li.wf-done:before{background:var(--brand-primary);border-color:var(--brand-primary)}
        ol.wf li.wf-current{color:var(--ink);font-weight:600}
        ol.wf li.wf-current:before{border-color:var(--brand-primary);box-shadow:0 0 0 3px var(--brand-primary-50)}
        ol.wf li.wf-stopped{color:var(--err);font-weight:600}
        ol.wf li.wf-stopped:before{border-color:var(--err);background:var(--err);box-shadow:0 0 0 3px var(--err-bg)}
      `}</style>
    </ol>
  );
}

/** Every status the complaint passed through, newest first, with who set it and when. */
function StatusHistory({ entries }: { entries: ComplaintStatusHistoryEntry[] }) {
  const { t } = useTranslation();
  return (
    <div style={{ borderTop: "1px solid var(--border)", marginTop: 6, paddingTop: 10 }}>
      <span style={fieldLabel}>{t("complaints.history.title", "History")}</span>
      <ol style={{ listStyle: "none", margin: "6px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        {[...entries].reverse().map((h, i) => (
          <li key={`${h.status}-${h.changedAt}-${i}`} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <ComplaintStatusBadge status={h.status} />
            <span style={historyMeta}>
              {formatDateTime(h.changedAt)} ·{" "}
              {h.changedByName ?? t("complaints.history.system", "System")}
            </span>
            {h.note && <span style={{ fontSize: 12.5, color: "var(--ink-2)", whiteSpace: "pre-wrap" }}>{h.note}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

function transitionErrorText(t: Translate, err: ApiError): string {
  switch (err.code) {
    case "COMPLAINT_DECISION_NOT_YOURS":
      return t("complaints.error.decisionNotYours", "O reklamaciji ove vrste odlučuje drugi direktor.");
    case "CORRECTIVE_ACTION_REQUIRED":
      return t(
        "complaints.error.measureRequired",
        "Osnovana reklamacija se ne zatvara bez korektivne mere.",
      );
    case "CORRECTIVE_ACTION_OUTSTANDING":
      return t("complaints.error.measureOutstanding", "Korektivna mera još nije sprovedena.");
    case "COMPLAINT_ALREADY_DECIDED":
      return t("complaints.error.alreadyDecided", "O reklamaciji je odlučeno, vrsta se više ne menja.");
    case "COMPLAINT_NOT_CLOSED":
      return t("complaints.error.notClosed", "Osporava se samo zatvorena reklamacija.");
    case "CONTEST_WINDOW_PASSED":
      return t("complaints.error.contestWindowPassed", "Rok za osporavanje ovog odgovora je prošao.");
    case "ALREADY_CONTESTED":
      return t("complaints.error.alreadyContested", "Ova reklamacija je već osporena.");
    case "REJECT_REASON_REQUIRED":
      return t("complaints.error.reasonRequired", "Razlog odbijanja je obavezan.");
    default:
      return err.message;
  }
}

function formatDate(iso: string, locale?: string) {
  // Kroz intlLocale, jer `sr` u Intl podrazumevano daje ćirilicu a portal je latiničan.
  return new Date(iso).toLocaleDateString(intlLocale(locale));
}

const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", padding: 18,
  display: "flex", flexDirection: "column", gap: 8,
};
const cardTitle: CSSProperties = { margin: 0, fontSize: 14, color: "var(--ink)" };
const fieldLabel: CSSProperties = {
  display: "block", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const kpiStrip: CSSProperties = {
  display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10,
};
const kpiTile: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", padding: "12px 14px",
  display: "flex", flexDirection: "column", gap: 4,
};
const kpiLabel: CSSProperties = {
  fontSize: 10, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-3)",
};
const kpiValueLg: CSSProperties = { fontSize: 18, color: "var(--ink)", display: "flex", alignItems: "center", gap: 6 };
const historyMeta: CSSProperties = { fontSize: 11.5, color: "var(--ink-3)" };
const crumbLink: CSSProperties = { color: "var(--ink-3)", textDecoration: "none" };
const crumbCurrent: CSSProperties = { color: "var(--ink-2)", fontWeight: 600 };
const messageCard: CSSProperties = {
  padding: 12, border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface)",
};
const avatar: CSSProperties = {
  display: "grid", placeItems: "center", width: 24, height: 24, borderRadius: 999,
  background: "var(--brand-primary)", color: "var(--on-primary)", fontSize: 10, fontWeight: 700,
};
